import { AxiosResponse } from 'axios'
import { logger } from '@/utils/logger'
import { AWSRUMErrorCapture } from '@/utils/awsRumErrorCapture'
import { ApiResponseHandler } from './ApiInterfaces'

const isDumpLog = (): boolean =>
  process.env.NODE_BUILD_ENV === 'dev' ||
  process.env.NODE_BUILD_ENV === 'dev2' ||
  process.env.NODE_BUILD_ENV === 'dev3' ||
  process.env.NODE_BUILD_ENV === 'stg' ||
  process.env.NODE_BUILD_ENV === 'stg1' ||
  process.env.NODE_BUILD_ENV === 'stg2'

// API Error Response (common)
export interface ResponseError {
  error: {
    messageId: string
    message: string
  }
}

export class ApiBase {
  /**
   * パス生成関数
   *
   * format文字列の :で始まる文字列を一致するKeyの値で置換する
   */
  public makePath(format: string, values: any): string {
    if (!values || typeof values !== 'object') {
      return format
    }
    return Object.keys(values).reduce(
      (acc: string, key: string): string => acc.replace(new RegExp(`:${key}`, 'g'), values[key]),
      format
    )
  }

  /**
   * API呼び出し時のヘルパー関数
   *
   * 成功時ハンドラと失敗時ハンドラをよしなに呼び出す
   * 構造サンプルを与えることで、受信データのキーチェックを行う
   * 構造チェックでエラーした場合は、API呼び出し元にnullが返る
   */
  protected callbackWrapper<HTTPResS, HTTPResE, R>(
    p: Promise<AxiosResponse<HTTPResS>>,
    s: ApiResponseHandler<HTTPResS, R> | undefined = undefined,
    e: ApiResponseHandler<HTTPResE, R> | undefined = undefined,
    validationSample: any = undefined,
    validationErrorSample: any = undefined
  ): Promise<R | null> {
    return p
      .then((value: AxiosResponse<HTTPResS>) => {
        if (isDumpLog()) {
          logger.debug('[callbackWrapper on success]', {
            path: value.config.url,
            method: value.config.method,
            params: value.config.params,
            data: value.data,
            sample: validationSample,
          })
        }

        if (validationSample !== undefined && value.data && !this.typeValidation(value.data, validationSample)) {
          AWSRUMErrorCapture.captureException({name:'TypeValidation', actual:value.data, expect: value.data })
          logger.warning('TypeValidation')
          return null
        }
        // 成功時ハンドラの呼び出し
        return s ? s(value.data, value.status) : null
      })
      .catch((reason: any) => {
        AWSRUMErrorCapture.captureException(reason)
        if (isDumpLog()) {
          logger.debug('[callbackWrapper on error]', reason)
          logger.debug('[callbackWrapper on error]', reason?.response)
          logger.debug('[callbackWrapper on error]', reason?.response?.data)
        }

        logger.warning(this.constructor.name, reason)

        if (!reason || !reason.response) {
          logger.warning('no response')
          return null
        }

        if (
          validationErrorSample !== undefined &&
          reason.response.data &&
          !this.typeValidation(reason.response.data, validationErrorSample)
        ) {
          logger.warning('TypeValidation')
          return null
        }

        // 失敗時ハンドラの呼び出し
        return e ? e(reason.response.data, reason.response.status) : null
      })
  }

  /**
   * 受信したデータの構造チェック
   *
   * 受信したデータに必須のパラメータが揃っているかチェックする
   * 必須のパラメータが足りていない場合に、falseを返す。
   */
  protected typeValidation(actualData: any, expectData: any): boolean {
    const canConvertToObject = (arg: any): boolean => {
      try {
        Object.keys(arg)
        return true
      } catch(error) {
        AWSRUMErrorCapture.captureException(error)
        return false
      }
    }

    // Keyの存在チェック
    // 引数に指定された情報(base,target)のkeyを比較する
    // 対象が配列の場合、対象内が入れ子の場合は、再帰的に呼び出して比較する
    const getDiffKeys = (base: any, target: any): string[] => {
      const keys: string[] = []

      const bIsArray = Array.isArray(base)
      const tIsArray = Array.isArray(target)

      // 構造体と配列の場合は、比較不能なので例外をThrowします
      if (bIsArray !== tIsArray && base != null && target != null) {
        logger.warning('TypeValidation', 'Different structure')
        throw new Error('object type error')
      }

      // 配列の場合は、各要素をチェック
      if (bIsArray && target != null) {
        return keys.concat(
          target.reduce((pv: string[], cv: any): string[] => {
            return base[0] ? pv.concat(getDiffKeys(base[0], cv)) : []
          }, [])
        )
      }

      // base, targetの両者がnullの場合は以降の検証を行わない
      if (!(canConvertToObject(base) && canConvertToObject(target))) {
        return keys
      }

      // オブジェクトの場合
      const targetKeys = Object.keys(target)
      const result = Object.keys(base).reduce<string[]>((pv: string[], cv: any): string[] => {
        if (!targetKeys.includes(cv)) {
          pv.push(cv)
        } else if (typeof base[cv] === 'object') {
          pv = pv.concat(getDiffKeys(base[cv], target[cv]))
        }
        return pv
      }, [])
      return result
    }

    // Keyが多い場合は情報だけ表示する
    try {
      if (!Array.isArray(actualData) || actualData.length !== 0) {
        const overKeys = getDiffKeys(actualData, expectData)
        if (overKeys.length) {
          logger.info('TypeValidation', 'overKeys:', overKeys)
          logger.info('actual', actualData)
        }
      }

      // Keyが足りていない場合にエラー
      const lessKeys = getDiffKeys(expectData, actualData)
      if (lessKeys.length) {
        logger.warning('TypeValidation', 'lessKeys:', lessKeys)
        logger.warning('actual', actualData)
        return false
      }
    } catch (error) {
      AWSRUMErrorCapture.captureException(error)
      logger.warning(error.message)
      return false
    }
    return true
  }
}
